/**
 * TruBeauté — Shareable Trouvé Card
 *
 * Turns any piece of Trouvé advice into a beautifully branded,
 * shareable PNG. Drop the component next to any tip and the share
 * button does the rest:
 *
 *   <TrouveShareCard
 *     tipText="Contour isn't about hiding who you are…"
 *     contextId="tip-blush-placement"
 *   />
 *
 * Flow:
 *   1. User clicks "Share this tip from Trouvé"
 *   2. We render an off-screen branded card via plain DOM
 *   3. html2canvas snapshots it to a PNG blob
 *   4. If Web Share API + canShare(files) is available → native share
 *   5. Else: trigger a PNG download
 *
 * Privacy:
 *   • No tip text is stored server-side. The PNG is generated entirely
 *     in the user's browser — Trouvé's words never round-trip our DB.
 *   • Three analytics events fire (share_card_clicked,
 *     share_card_downloaded, share_card_shared) but they only
 *     identify the contextId / cta — never the tip content itself.
 */
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Download, Check, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import { trackFP } from "../lib/firstPartyAnalytics";

const SHARE_URL = "https://trubeaute.com"; // canonical user-facing URL

/**
 * Render the off-screen branded card DOM. Kept as a pure-JSX block
 * (NOT a portal) so html2canvas can read its computed styles. We hide
 * it via fixed positioning + visually-hidden offset so the user never
 * sees the snapshot DOM.
 */
function ShareCardCanvas({ cardRef, tipText, attribution }) {
  return (
    <div
      ref={cardRef}
      style={{
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        width: "1080px",
        height: "1080px",
        background:
          "linear-gradient(135deg, #F8F1ED 0%, #EBD6D2 50%, #D5A8A4 100%)",
        fontFamily: "'Playfair Display', Georgia, serif",
        padding: "120px 100px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: "#2A1E22",
      }}
      data-testid="trouve-share-card-canvas"
    >
      {/* Brand mark */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, #9B6A6C 0%, #D5A8A4 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "28px",
            fontStyle: "italic",
          }}
        >
          T
        </div>
        <div style={{ fontSize: "20px", letterSpacing: "0.18em", color: "#9B6A6C" }}>
          TROUVÉ
        </div>
      </div>

      {/* Quote */}
      <div>
        <div
          style={{
            fontSize: "44px",
            lineHeight: "1.35",
            fontStyle: "italic",
            color: "#2A1E22",
            marginBottom: "32px",
            // Limit very long quotes — html2canvas won't auto-shrink
            maxHeight: "560px",
            overflow: "hidden",
          }}
        >
          “{tipText}”
        </div>
        <div
          style={{
            fontSize: "20px",
            color: "#5A4347",
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.1em",
          }}
        >
          {attribution || "— TROUVÉ"}
        </div>
      </div>

      {/* Footer: brand + URL */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "1px solid rgba(42, 30, 34, 0.15)",
          paddingTop: "32px",
        }}
      >
        <div
          style={{
            fontSize: "28px",
            color: "#2A1E22",
            fontStyle: "italic",
          }}
        >
          TruBeauté
        </div>
        <div
          style={{
            fontSize: "20px",
            color: "#9B6A6C",
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.06em",
          }}
        >
          trubeaute.com
        </div>
      </div>
    </div>
  );
}

/**
 * Public component. Drop next to any tip with a `tipText` prop.
 *
 *   contextId is an analytics handle — e.g., "trouve-tea-blush",
 *   "demo-result-step-3" — so we can see WHICH cards drive shares.
 */
export default function TrouveShareCard({
  tipText,
  attribution = "— TROUVÉ",
  contextId = "unknown",
  className = "",
  label = null,
  variant = "default",
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | working | shared | downloaded | error

  const buildPngBlob = async () => {
    if (!cardRef.current) throw new Error("share card DOM not ready");
    const canvas = await html2canvas(cardRef.current, {
      scale: 1, // 1080×1080 is plenty — bigger = slow on mobile
      useCORS: true,
      backgroundColor: null,
      logging: false,
    });
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
        "image/png",
        0.92,
      );
    });
  };

  const onShare = async () => {
    setStatus("working");
    // Funnel event #1 — every click counts even if the share ultimately
    // fails. This lets us measure intent vs. completion separately.
    trackFP("share_card_clicked", { context: contextId });

    let blob;
    try {
      blob = await buildPngBlob();
    } catch (err) {
      setStatus("error");
      return;
    }

    const file = new File([blob], `trouve-tip-${Date.now()}.png`, {
      type: "image/png",
    });

    // Path 1: native Web Share API with files (mobile Safari, Chrome)
    if (
      typeof navigator !== "undefined" &&
      navigator.canShare &&
      navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({
          files: [file],
          title: "Trouvé · TruBeauté",
          text: tipText,
        });
        trackFP("share_card_shared", { context: contextId, channel: "web_share" });
        setStatus("shared");
        // Reset back to idle after a moment so users can share again
        setTimeout(() => setStatus("idle"), 2200);
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to download
      }
    }

    // Path 2: PNG download
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trouve-tip-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      trackFP("share_card_downloaded", { context: contextId });
      setStatus("downloaded");
      setTimeout(() => setStatus("idle"), 2200);
    } catch (err) {
      setStatus("error");
    }
  };

  // Minimal "pill" variant for use inside tight TrouveTea panels
  const isPill = variant === "pill";

  return (
    <>
      <button
        type="button"
        onClick={onShare}
        disabled={status === "working"}
        className={
          className ||
          (isPill
            ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#9B6A6C]/10 hover:bg-[#9B6A6C]/20 text-[#9B6A6C] text-xs font-medium transition-colors disabled:opacity-60"
            : "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#9B6A6C] hover:bg-[#7B4A4C] text-white text-sm transition-colors disabled:opacity-60")
        }
        data-testid={`trouve-share-btn-${contextId}`}
        aria-label={t("trouveShare.ariaLabel")}
      >
        {status === "working" ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : status === "shared" || status === "downloaded" ? (
          <Check className="h-3.5 w-3.5" />
        ) : status === "downloaded" ? (
          <Download className="h-3.5 w-3.5" />
        ) : (
          <Share2 className="h-3.5 w-3.5" />
        )}
        <span>
          {status === "working"
            ? t("trouveShare.working")
            : status === "shared"
              ? t("trouveShare.shared")
              : status === "downloaded"
                ? t("trouveShare.downloaded")
                : status === "error"
                  ? t("trouveShare.error")
                  : label || t("trouveShare.cta")}
        </span>
      </button>
      <ShareCardCanvas
        cardRef={cardRef}
        tipText={tipText}
        attribution={attribution}
      />
    </>
  );
}
